import { useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { useBottomSheetContext } from '@/shared/contexts/BottomSheetContext'
import { useRecords } from '@/shared/hooks'
import { useRewardsStore } from '@/shared/store/useRewardsStore'
import { alert } from '@/shared/ui/dialog'
import { FormControl } from '@/shared/ui/Form'
import { MobileSheet } from '@/shared/ui/MobileSheet'
import { useResourceContext, Resource } from '@/shared/ui/Resource'

import { useDigitalProductStore } from '../hook'
import { sortRewardRequests } from '../lib/sortRewardRequests'
import { DigitalProductStoreBottomSheetDescription } from './Description'
import { DigitalProductStoreBottomSheetFooter } from './Footer'
import { DigitalProductStoreBottomSheetForm } from './Form'
import { validationSchema } from './Form/validationSchema'
import { DigitalProductStoreBottomSheetImages } from './Images'
import { DigitalProductStoreBottomSheetPrice } from './Price'
import { DigitalProductStoreBottomSheetRewardRequests } from './RewardRequests'
import { DigitalProductStoreBottomSheetVariations } from './Variations'

const DigitalProductStoreBottomSheet = ({ rewardId, rewardItemId, onHide, onDismiss }) => {
  const { reward, allRewardRequests, refetchRewardRequests, refetchRewards } =
    useRewardsStore({
      id: rewardId
    })
  const rewardRequestsWithCanceled = sortRewardRequests(
    allRewardRequests.filter(request => request.reward_id === rewardId)
  )

  const [isClaim, setIsClaim] = useState(false)

  const { claim, polling, refetchStoreStatus } = useDigitalProductStore()
  const { hideBottomSheet } = useBottomSheetContext()

  /* `digitalProduct` (e o `store_hours` que ele carrega) não tem poll — só
     5min de staleTime, sem gatilho automático (ver useDigitalProduct.ts). Quem
     abriu o app antes do produtor ajustar a janela via `isClosed` preso no
     estado antigo. Abrir o sheet é o momento em que essa resposta importa de
     verdade, e é barato revalidar só aqui em vez de repor o poll global. */
  useEffect(() => {
    refetchStoreStatus?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClose = () => {
    if (onDismiss) {
      onDismiss()
      return
    }

    hideBottomSheet()
    onHide?.()
  }

  const methods = useForm({
    resolver: yupResolver(DigitalProductStoreBottomSheet.Form.validationSchema),
    defaultValues: {
      reward_item_id: rewardItemId,
      quantity: 1,
      reward_id: reward.id
    }
  })

  const onSubmit = async data => {
    setIsClaim(true)

    const { rewardRequest } = await claim(data)

    if (rewardRequest) {
      polling({
        id: rewardRequest.id,
        onSuccess: rewardRequest => {
          if (rewardRequest.status == 'out_of_stock') {
            toast.error('Sem estoque', {
              description: 'Este item não está mais disponível, tente outro'
            })
          } else if (rewardRequest.status == 'insufficient_coins') {
            void alert({
              variant: 'destructive',
              title: 'Moedas insuficientes',
              description:
                'Você não tem moedas suficientes para reservar esta recompensa'
            })
          } else if (rewardRequest.status == 'approved') {
            toast.success('Tudo certo!', {
              description: 'Recompensa reservada com sucesso!'
            })
          }
          // Atualiza requests e estoque dos itens em qualquer desfecho — em
          // out_of_stock o item passa a refletir o esgotamento na UI.
          refetchRewardRequests()
          refetchRewards()
          methods.reset()
          setIsClaim(false)
        }
      })
    } else {
      setIsClaim(false)
    }
  }

  return (
    <Resource.Root
      type="Reward"
      {...reward}
      rewardRequestsWithCanceled={rewardRequestsWithCanceled}
      refetchRewardRequests={refetchRewardRequests}
      isClaim={isClaim}
    >
      <FormProvider {...methods}>
        <MobileSheet
          open
          onClose={handleClose}
          title={reward?.name}
          ariaLabel={reward?.name}
          className="!max-w-[400px]"
          footer={
            <DigitalProductStoreBottomSheet.Footer>
              <DigitalProductStoreBottomSheet.Form.QuantityField />
            </DigitalProductStoreBottomSheet.Footer>
          }
        >
          {reward?.reward_category && (
            <p className="-mt-1 mb-3 block text-center text-caps font-bold uppercase text-gray-500">
              {reward.reward_category.name}
            </p>
          )}
          <form id="reward_request" onSubmit={methods.handleSubmit(onSubmit)}>
            <FormControl name="reward_item_id" type="hidden" />
            <FormControl name="reward_id" type="hidden" />
            <div className="flex flex-col gap-4">
              <DigitalProductStoreBottomSheet.Images />
              <DigitalProductStoreBottomSheet.Price />
              <DigitalProductStoreBottomSheet.Variations />
              <DigitalProductStoreBottomSheet.RewardRequests />
              <DigitalProductStoreBottomSheet.Description />
            </div>
          </form>
        </MobileSheet>
      </FormProvider>
    </Resource.Root>
  )
}

DigitalProductStoreBottomSheet.Variations =
  DigitalProductStoreBottomSheetVariations
DigitalProductStoreBottomSheet.Images = DigitalProductStoreBottomSheetImages
DigitalProductStoreBottomSheet.Price = DigitalProductStoreBottomSheetPrice
DigitalProductStoreBottomSheet.Description =
  DigitalProductStoreBottomSheetDescription
DigitalProductStoreBottomSheet.Footer = DigitalProductStoreBottomSheetFooter
DigitalProductStoreBottomSheet.Form = DigitalProductStoreBottomSheetForm
DigitalProductStoreBottomSheet.RewardRequests =
  DigitalProductStoreBottomSheetRewardRequests

export { DigitalProductStoreBottomSheet }
